import React from "react";
import { Reveal, Section, Container, EyebrowLarge } from "./Shared";
import { Target, Users, ShieldCheck, Zap, Link, BadgeCheck, Gem, Sprout, Rocket } from "lucide-react";
import { motion } from "framer-motion";



const values = [
    {
        title: "Connection with Purpose",
        desc: "We align intentions, capabilities and goals — ensuring every intro has meaning.",
        img: require('../../assets/ivani/ov1.jpg'),
        icon: Link
    },
    {
        title: "Integrity in Action",
        desc: "We operate with honesty and transparency — always choosing what’s right over what’s easy.",
        img: require('../../assets/ivani/ov2.jpg'),
        icon: BadgeCheck
    },
    {
        title: "Curated Excellence",
        desc: "Every partner and solution is hand-picked to fit your organization’s unique journey.",
        img: require('../../assets/ivani/ov3.jpg'),
        icon: Gem
    },
    {
        title: "Collaborative Growth",
        desc: "We grow by helping others grow — building long-term, trust-driven partnerships.",
        img: require('../../assets/ivani/ov4.jpg'),
        icon: Sprout
    },
    {
        title: "Impact over Activity",
        desc: "We prioritise outcomes — measuring success by the difference we make, not the volume of work.",
        img: require('../../assets/ivani/ov5.jpg'),
        icon: Rocket
    }
];

export default function Vision() {
    return (
        <Section id="vision" className="relative font-body py-24 md:py-32">
            {/* Background Ambience */}
            <div className="absolute top-1/4 left-[-20%] w-[800px] h-[800px] rounded-full blur-[120px] pointer-events-none opacity-[0.15]" style={{ background: 'var(--ivani-color)' }} />
            <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full blur-[100px] pointer-events-none opacity-[0.1]" style={{ background: 'var(--ivani-color)' }} />

            <Container>
                {/* 1. OUR PURPOSE SECTION */}
                <Reveal>
                    <div className="text-center mb-12 md:mb-16">
                        <EyebrowLarge>OUR PURPOSE</EyebrowLarge>
                    </div>
                </Reveal>

                <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24 md:mb-32">

                    {/* Text Layer (Left) - Unboxed */}
                    <div className="order-2 lg:order-1 pl-4 md:pl-0">
                        <Reveal>
                            {/* <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-8 tracking-tight leading-[1.1]">
                                Potential to <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-zinc-500">Performance.</span>
                            </h2> */}

                            {/* <div className="h-1 w-24 bg-gradient-to-r from-white/30 to-transparent rounded-full mb-10"></div> */}

                            <p className="text-xl md:-rotate-4 md:text-2xl text-zinc-300 font-light leading-relaxed mb-12 max-w-xl">
                                To enable organizations to move from potential to performance — by connecting them with the right ideas, people, and partnerships that unlock growth.
                            </p>

                            <div className="flex flex-wrap gap-3">
                                {[
                                    { label: "Outcome-first", icon: Target },
                                    { label: "Curated partners", icon: Users },
                                    { label: "Trusted facilitation", icon: ShieldCheck },
                                ].map((tag, i) => (
                                    <motion.span
                                        key={i}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: i * 0.1, duration: 0.5 }}
                                        className="px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm font-medium text-white/90 transition-all hover:bg-white/10 hover:border-white/20 cursor-default backdrop-blur-md flex items-center gap-2"
                                    >
                                        <tag.icon className="w-4 h-4 text-white/70" />
                                        {tag.label}
                                    </motion.span>
                                ))}
                            </div>
                        </Reveal>
                    </div>

                    {/* Image Layer (Right) */}
                    <div className="relative order-1 lg:order-2">
                        <motion.div
                            initial={{ opacity: 0, x: 200, scale: 0.5, rotate: 15, skewY: 12, filter: "blur(20px)" }}
                            whileInView={{ opacity: 1, x: 0, scale: 1, rotate: 0, skewY: 0, filter: "blur(0px)" }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ type: "spring", stiffness: 50, damping: 15, mass: 1.2, delay: 0.2 }}
                        >
                            <div className="relative rounded-[40px] overflow-hidden shadow-2xl group ring-1 ring-white/10 lg:rotate-2 lg:hover:rotate-0 transition-transform duration-700">
                                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-20 transition-opacity duration-700 mix-blend-overlay z-10"></div>
                                <img
                                    src={require('../../assets/ivani/op.jpg')}
                                    alt="Our Purpose"
                                    className="w-full aspect-[4/3] object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

                                {/* Decorative Floating Badge on Image */}
                                <div className="absolute bottom-8 right-8 z-20 hidden md:block">
                                    <div className="glass-card px-6 py-3 rounded-full border border-white/20 flex items-center gap-3 bg-black/40 backdrop-blur-md">
                                        <div className="relative flex items-center justify-center">
                                            <div className="absolute inset-0 bg-green-500/20 blur-md rounded-full animate-pulse"></div>
                                            <Zap className="w-4 h-4 text-green-400 relative z-10" />
                                        </div>
                                        <span className="text-white text-sm font-medium tracking-wide">Strategic clarity</span>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* 1.5. CORE PRINCIPLES (Legacy Grid) */}
                <div className="relative mb-32 md:mb-40">
                    <div className="text-center mb-16 md:mb-20">
                        <span className="inline-block py-1 px-3 rounded-full bg-white/5 border border-white/10 text-xs font-medium tracking-widest uppercase backdrop-blur-md mb-6 text-white/80">
                            Core Principles
                        </span>
                        <EyebrowLarge>OUR VALUES</EyebrowLarge>

                        {/* <h3 className="font-heading text-3xl md:text-5xl font-bold text-white mb-6">Our Values</h3> */}
                        <p className="text-zinc-400 max-w-2xl mx-auto text-lg font-light">
                            The foundation of how we work and who we are.
                        </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-6 md:gap-8">
                        {values.map((v, i) => (
                            <Reveal key={i} className="w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.33%-2rem)]">
                                <div className="group h-full glass-card p-4 rounded-3xl border border-white/10 hover:border-white/20 transition-all duration-500 hover:shadow-xl flex flex-col relative overflow-hidden hover:-translate-y-2 hover:scale-[1.02]">
                                    {/* Noise Texture */}
                                    <div className="absolute inset-0 bg-noise opacity-10 pointer-events-none z-0 mix-blend-overlay"></div>

                                    {/* Large Background Icon */}
                                    <v.icon className="absolute -bottom-6 -right-6 w-32 h-32 text-white opacity-5 -rotate-12 pointer-events-none transition-transform duration-700 group-hover:scale-110 z-0" />

                                    <div className="h-48 rounded-2xl overflow-hidden relative mb-4 z-10">
                                        <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors z-10"></div>
                                        <img
                                            src={v.img}
                                            alt={v.title}
                                            className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="px-0 pb-2 flex-1 flex flex-col relative z-20">
                                        <h4 className="font-heading text-xl font-bold text-white mb-3 transition-colors flex items-center gap-2">
                                            <v.icon className="w-5 h-5 text-white" />
                                            {v.title}
                                        </h4>
                                        <p className="text-zinc-400 text-base md:text-lg max-w-2xl mx-auto font-light">
                                            {v.desc}
                                        </p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>


            </Container>
        </Section>
    );
}
